// src/services/coingecko.js
// Rate-limit friendly CoinGecko client with localStorage cache, SWR, and exponential backoff.

const BASE = "https://api.coingecko.com/api/v3";

// ---- helpers ----
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const now = () => Date.now();

function readCache(key, maxAgeMs) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (obj.t + maxAgeMs < now()) return null; // stale
    return obj.v;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ t: now(), v: value })
    );
  } catch {}
}

async function fetchWithBackoff(url, { signal, maxRetries = 3 } = {}) {
  let attempt = 0;
  while (true) {
    try {
      const res = await fetch(url, { headers: { accept: "application/json" }, signal });
      if (res.status === 429) {
        // too many requests → exponential backoff
        const waitMs = Math.min(1000 * Math.pow(2, attempt), 10_000);
        await sleep(waitMs);
        attempt++;
        if (attempt > maxRetries) throw new Error("429 Too Many Requests");
        continue;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      if (err.name === "AbortError") throw err;
      attempt++;
      if (attempt > maxRetries) throw err;
      await sleep(500 * attempt);
    }
  }
}

/**
 * SWR-like: return cached immediately (if exists), then refresh in background.
 * onRefresh(data) will be called when fresh data arrives.
 */
async function swrFetch(url, { cacheKey, maxAgeMs, onRefresh, signal, maxRetries } = {}) {
  const cached = readCache(cacheKey, maxAgeMs);
  // Kick off a refresh in background
  (async () => {
    try {
      const fresh = await fetchWithBackoff(url, { signal, maxRetries });
      writeCache(cacheKey, fresh);
      onRefresh && onRefresh(fresh);
    } catch {
      // keep cached
    }
  })();
  return cached;
}

// ---- public API ----

// markets (top list)
export async function getMarkets({ vs = "usd", order = "market_cap_desc", perPage = 50, page = 1, changes = "1h,24h,7d", signal } = {}, { onRefresh } = {}) {
  const params = new URLSearchParams({
    vs_currency: vs,
    order,
    per_page: String(perPage),
    page: String(page),
    price_change_percentage: changes,
  });
  const url = `${BASE}/coins/markets?${params.toString()}`;
  const cacheKey = `cg:markets:${vs}:${order}:${perPage}:${page}:${changes}`;
  // markets: TTL 60s
  const cached = await swrFetch(url, { cacheKey, maxAgeMs: 60_000, onRefresh, signal, maxRetries: 3 });
  // If nothing cached, do a blocking fetch once
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}

// trending ids → then markets for those ids
export async function getTrending({ vs = "usd", signal } = {}, { onRefresh } = {}) {
  const tUrl = `${BASE}/search/trending`;
  const tKey = `cg:trending`;
  const trending = await swrFetch(tUrl, { cacheKey: tKey, maxAgeMs: 120_000, onRefresh: null, signal, maxRetries: 3 });
  async function hydrate(ids) {
    if (!ids?.length) return [];
    const params = new URLSearchParams({
      vs_currency: vs,
      ids: ids.join(","),
      order: "market_cap_desc",
      per_page: "100",
      page: "1",
      price_change_percentage: "24h",
    });
    const mUrl = `${BASE}/coins/markets?${params.toString()}`;
    const mKey = `cg:trending:markets:${vs}:${ids.join(",")}`;
    const cachedMarkets = await swrFetch(mUrl, { cacheKey: mKey, maxAgeMs: 120_000, onRefresh, signal, maxRetries: 3 });
    if (!cachedMarkets) {
      const fresh = await fetchWithBackoff(mUrl, { signal, maxRetries: 3 });
      writeCache(mKey, fresh);
      return fresh;
    }
    return cachedMarkets;
  }
  if (!trending) {
    // first time: do a blocking chain
    const tJson = await fetchWithBackoff(tUrl, { signal, maxRetries: 3 });
    writeCache(tKey, tJson);
    const ids = (tJson?.coins || []).map(c => c?.item?.id).filter(Boolean);
    return await hydrate(ids);
  }
  const ids = (trending?.coins || []).map(c => c?.item?.id).filter(Boolean);
  return await hydrate(ids);
}

// coin details (side panel)
export async function getCoinDetails(id, { signal } = {}, { onRefresh } = {}) {
  if (!id) return null;
  const url = `${BASE}/coins/${id}?localization=false&tickers=false&market_data=true&community_data=true&developer_data=true&sparkline=true`;
  const cacheKey = `cg:coin:${id}`;
  // coin details: TTL 180s
  const cached = await swrFetch(url, { cacheKey, maxAgeMs: 180_000, onRefresh, signal, maxRetries: 3 });
  if (!cached) {
    const fresh = await fetchWithBackoff(url, { signal, maxRetries: 3 });
    writeCache(cacheKey, fresh);
    return fresh;
  }
  return cached;
}
